import React, {Fragment, useRef, useState} from 'react'
import {
  AddToCalendarBox,
  CentralBox,
  DashboardBox,
  Divider,
  DividerNoMargin,
  DivisionBox,
  LeftBox,
  NextVWButtonBox,
  NextVWStyledIcon,
  NextVWText,
  NextVWTextBox,
  NextWorkshopBox,
  SpacingBox,
  RightBox,
  SectionBox,
  SectionTitle,
  SubTitle,
  Title,
  FAQBox,
  FAQItem,
  FAQDescription,
  NextVWFlexBox,
  StatusCardBox,
} from './styles'
import Sidenav from '../../components/Sidenav'
import {Link as RouterLink} from 'react-router-dom'
import ExamStatusCard from '../../components/Cards/ExamStatusCard'
import DivisionProgress from '../../components/DivisionProgress'
import ProvenProcess from '../../../images/icons/new-dashboard/proven-process-icon.svg'
import NextVWIcon from '../../../images/icons/new-dashboard/next-vw-icon.svg'
import AddToCalendar from '../../../images/icons/new-dashboard/add-calendar-icon.svg'
import ReferralBanner from '../../components/ReferralBanner'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {useWindowDimensions} from '../../../hooks/useWindowDimensions'
import {Course, Organization} from '../../../redux/courses'
import {
  EligibleSurvey,
  Member,
  selectMemberAccess,
} from '../../../redux/members'
import {useAuth} from '../../../components/AuthProvider'
import {Attempt} from '../../../redux/reportExamStatus'
import {formatWorkshopDate} from '../../../utils/formatWorkshopDates'
import {CalendarEvent, ics} from 'calendar-link'
import {Workshop} from '../../../redux/workshop'
import moment from 'moment-timezone'
import {SecondaryLinkButton} from '../../../styles/global'
import {useSelector} from 'react-redux'
import {useAppDispatch} from '../../../redux/configureStore'
import {Button} from '../../../components/button'
import ExpireSurveyDialog from '../../../components/ExpireSurveyDialog'
import LiveNow from '../../../images/icons/new-dashboard/live-now.svg'
import CalendarAlert from '../../../images/icons/calendar-alert-icon.svg'
import {CalendarAlertBox} from '../../../pages/VirtualWorkshops/styles'
import {handleUpgrade} from '../../../utils/upgradeHelper'
import ChallengeCard from '../../components/Cards/ChallengeCard'
import Challenge30Logo from '../../../images/study-challenge-logo.svg'
import {selectActiveChallenge} from '../../../redux/challenges'

type Props = {
  organization?: Organization
  member?: Member
  examReports?: Attempt[]
}

const checkIfSurveyIsEligible = (
  surveys: EligibleSurvey[],
  courses: Course[],
): boolean => {
  const PROVEN_PROCESS_SURVEY_ID = 1
  let threeOrMoreDays = false

  for (const course of courses) {
    course.studyGuides?.forEach(guide => {
      if (guide.completeDays && guide.completeDays >= 3) {
        threeOrMoreDays = true
      }
    })
  }

  for (const survey of surveys) {
    if (
      survey &&
      survey.surveyId == PROVEN_PROCESS_SURVEY_ID &&
      survey.eligible &&
      threeOrMoreDays
    ) {
      return true
    }
  }

  return false
}

const View = ({organization, member, examReports}: Props) => {
  const auth = useAuth()
  const {isMobile} = useWindowDimensions()
  const dispatch = useAppDispatch()
  const access = useSelector(selectMemberAccess)
  const challenge = useSelector(selectActiveChallenge)

  const [isOpen, setIsOpen] = useState(member?.showExpireSurvey)
  const [isSurveyDialogOpen, setIsSurveyDialogOpen] = useState(
    checkIfSurveyIsEligible(
      member?.eligibleSurveys ?? [],
      organization?.courses ?? [],
    ),
  )

  const uniqueReports: Attempt[] = Object.values(
    (examReports ?? [])
      .filter(report => report.courseId)
      .reduce<Record<string, Attempt>>((acc, report) => {
        if (
          !acc[report.courseId] ||
          new Date(report.updatedAt) > new Date(acc[report.courseId].updatedAt)
        ) {
          acc[report.courseId] = report
        }
        return acc
      }, {}),
  )

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const report = uniqueReports.find(item => {
    const itemDate = new Date(item.examDate)
    itemDate.setHours(0, 0, 0, 0)
    return itemDate <= today && item.passed === null
  })

  const faqs = [
    {
      title: <span>What is the Guided Study Process?</span>,
      description: (
        <span>
          The Guided Study Process is your daily study plan designed to help you
          pass your ARE exams on the first try. Developed in collaboration with
          learning scientists and architects, it ensures you're studying the
          right content and helps you retain what you learn. With a clear daily
          plan, you'll know exactly what to study each day, keeping you on track
          for success.
        </span>
      ),
    },
    {
      title: <span>How does the Guarantee work?</span>,
      description: (
        <span>
          Purchase an Expert subscription, complete the entire course, and in
          the unlikely event you don't pass, we'll contact NCARB to pay for your
          retake.
        </span>
      ),
    },
    {
      title: (
        <span>How do I choose between the Shorter and Longer Study Plans?</span>
      ),
      description: (
        <>
          <span>
            • The Shorter Plan is designed for those who can commit 2-3 hours of
            study per day and want to move through the material faster.
          </span>
          <br />
          <br />
          <span>
            • The Longer Plan is ideal for individuals with busier schedules,
            requiring only 30 minutes of study each day, but offers a slower
            pace for more flexibility.
          </span>
        </>
      ),
    },
    {
      title: (
        <span>Can I switch between the Shorter and Longer Study Plans?</span>
      ),
      description: (
        <span>
          Yes! You can adjust your study plan as needed to fit your schedule and
          study preferences.
        </span>
      ),
    },
  ]

  const [expandedItems, setExpandedItems] = useState(faqs.map(() => false))
  const contentRefs = useRef<(HTMLDivElement | null)[]>([])

  const toggleExpand = (index: number) => {
    setExpandedItems(prev =>
      prev.map((isExpanded, i) => (i === index ? !isExpanded : isExpanded)),
    )
  }

  const courses = organization?.courses.filter(course => {
    return ['PcM', 'PjM', 'PA', 'PPD', 'PDD', 'CE'].includes(
      course.abbreviation,
    )
  })

  const inProgressCourses = courses?.filter(course => {
    return course.studyGuides?.find(sg => sg.inProgress)
  })

  const notInProgressCourses = courses?.filter(course => {
    return !course.studyGuides?.find(sg => sg.inProgress)
  })

  const randomIndex = Math.floor(
    Math.random() * (notInProgressCourses?.length ?? 1),
  )
  const nextWorkshop = inProgressCourses
    ? inProgressCourses[0]?.nextWorkshop
    : notInProgressCourses
    ? notInProgressCourses[randomIndex]?.nextWorkshop
    : undefined

  const getEventData = (workshop?: Workshop) => {
    if (!workshop) return {} as CalendarEvent

    const startDate = new Date(workshop?.date)
    const endDate = new Date(workshop?.date)
    endDate.setHours(endDate.getHours() + 2)

    const centralStartDate = moment(startDate)
      .tz('America/Chicago')
      .format('YYYY-MM-DD HH:mm:ss ZZ')
    const centralEndDate = moment(endDate)
      .tz('America/Chicago')
      .format('YYYY-MM-DD HH:mm:ss ZZ')

    const response: CalendarEvent = {
      title: workshop?.title,
      description: workshop?.description,
      start: centralStartDate,
      end: centralEndDate,
      location: window.location.href,
    }

    return response
  }
  const handleDownload = (workshop?: Workshop) => {
    const response = decodeURIComponent(
      ics(getEventData(workshop)).replace(
        /^data:text\/calendar;charset=utf8,/,
        '',
      ),
    )

    const blob = new Blob([response], {type: 'text/calendar;charset=utf-8'})
    const url = window.URL.createObjectURL(blob)
    window.open(url)
  }

  const reportCourse = courses?.find(c => c.id === report?.courseId)

  const hasSgAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.study_guides?.value) === 1
      : false

  const hasVwAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.workshops?.value) === 1
      : false

  const getExamDate = () => {
    if (report) {
      const examDate = new Date(report.examDate)
      examDate.setHours(examDate.getHours() + 12)
      return examDate.toLocaleDateString('en-US')
    }

    return ''
  }

  return (
    <DashboardBox>
      <LeftBox>
        <Sidenav />
      </LeftBox>
      <CentralBox>
        <Title>
          {auth.isAuthenticated && member?.firstName && member?.lastName
            ? `Welcome, ${member?.firstName} ${member?.lastName}`
            : 'Welcome'}
          {challenge?.hasEntry && (
            <img src={Challenge30Logo} alt="30 Day Challenge" />
          )}
        </Title>
        {isMobile && (
          <StatusCardBox>
            <ExamStatusCard examReports={uniqueReports} />
          </StatusCardBox>
        )}
        <Divider style={{backgroundColor: '#B5B5B5'}} />
        <SubTitle>
          Learning
          <div>
            <img src={ProvenProcess} alt="Guided Study Process" />
            {!hasSgAccess && auth.isAuthenticated && (
              <Button
                color="secondary"
                children={'Upgrade to Expert'}
                onClick={() =>
                  handleUpgrade(
                    dispatch,
                    auth,
                    member,
                    'proven process paywall',
                  )()
                }
              />
            )}
          </div>
        </SubTitle>

        {report && (
          <NextWorkshopBox>
            <NextVWFlexBox>
              <NextVWTextBox>
                <NextVWText variant="title">How did your exam go?</NextVWText>
                <NextVWText variant="body">
                  Your {reportCourse?.abbreviation} exam was scheduled for{' '}
                  {getExamDate()}. We’d love to hear about your results and how
                  you’re progressing —{' '}
                  <RouterLink
                    to={`/profile/settings/are_progress/${reportCourse?.abbreviation.toLocaleLowerCase()}`}
                  >
                    let us know!
                  </RouterLink>
                </NextVWText>
              </NextVWTextBox>
            </NextVWFlexBox>
            <NextVWButtonBox>
              <SecondaryLinkButton
                component={RouterLink}
                to={`/profile/settings/are_progress/${reportCourse?.abbreviation.toLocaleLowerCase()}`}
              >
                Register Now
              </SecondaryLinkButton>
            </NextVWButtonBox>
          </NextWorkshopBox>
        )}

        <SectionBox>
          <SectionTitle>ARE 5.0</SectionTitle>

          {inProgressCourses && inProgressCourses.length > 0 && (
            <DivisionBox>
              {inProgressCourses?.map((course, key) => {
                const reportStatus = uniqueReports.find(
                  ur => ur.courseId === course.id,
                )

                return (
                  <React.Fragment key={`inpsg-${key}`}>
                    <DivisionProgress
                      courseId={course.id}
                      division={course.abbreviation}
                      studyGuides={course.studyGuides}
                      reportStatus={reportStatus}
                    />
                    {key + 1 < inProgressCourses.length && (
                      <Divider style={{margin: '8px 0'}} />
                    )}
                  </React.Fragment>
                )
              })}
            </DivisionBox>
          )}

          <DivisionBox>
            {notInProgressCourses?.map((course, key) => {
              const reportStatus = uniqueReports.find(
                ur => ur.courseId === course.id,
              )

              return (
                <React.Fragment key={`ninpsg-${key}`}>
                  <DivisionProgress
                    courseId={course.id}
                    division={course.abbreviation}
                    studyGuides={course.studyGuides}
                    reportStatus={reportStatus}
                  />
                  {key + 1 < notInProgressCourses.length && (
                    <Divider style={{margin: '8px 0'}} />
                  )}
                </React.Fragment>
              )
            })}
          </DivisionBox>
        </SectionBox>
        {/* <SectionBox>
          <SectionTitle>
            California Architects <NewLabel>New</NewLabel>
          </SectionTitle>
          <DivisionBox>
            <DivisionProgress inProgress={false} division="CSE" />
          </DivisionBox>
        </SectionBox> */}
        {nextWorkshop && (
          <NextWorkshopBox>
            <NextVWFlexBox>
              <NextVWStyledIcon src={NextVWIcon} alt="Next Virtual Workshop" />
              <NextVWTextBox>
                <NextVWText variant="title">
                  Next Virtual Workshop{' '}
                  {nextWorkshop.isLaunchEnabled && (
                    <img src={LiveNow} alt="Live" />
                  )}
                </NextVWText>
                <NextVWText variant="body">
                  {nextWorkshop.title}
                  {nextWorkshop.displayAlert && (
                    <CalendarAlertBox>
                      <img src={CalendarAlert} alt="Date Changed" /> Date
                      Changed
                    </CalendarAlertBox>
                  )}
                </NextVWText>
                <NextVWText variant="hint">
                  {formatWorkshopDate(nextWorkshop.date)}
                </NextVWText>
              </NextVWTextBox>
            </NextVWFlexBox>
            <NextVWButtonBox>
              {hasVwAccess ? (
                <>
                  <AddToCalendarBox
                    onClick={() => handleDownload(nextWorkshop)}
                  >
                    <img src={AddToCalendar} alt="Add to Calendar" />
                    Calendar
                  </AddToCalendarBox>

                  <SecondaryLinkButton
                    component={RouterLink}
                    to={`/virtual-workshops/${nextWorkshop.courseId}`}
                  >
                    Register Now
                  </SecondaryLinkButton>
                </>
              ) : (
                <>
                  <Button
                    color="secondary"
                    children={'Upgrade to Expert'}
                    onClick={() =>
                      handleUpgrade(
                        dispatch,
                        auth,
                        member,
                        'virtual workshops paywall',
                      )()
                    }
                  />
                </>
              )}
            </NextVWButtonBox>
          </NextWorkshopBox>
        )}
        <SpacingBox>
          <ReferralBanner />
        </SpacingBox>
        <SpacingBox>
          <SubTitle>Frequently asked questions</SubTitle>
          <FAQBox>
            {faqs.map((faq, key) => {
              return (
                <Fragment key={`faq-${key}`}>
                  <FAQItem onClick={() => toggleExpand(key)}>
                    {faq.title}
                    {expandedItems[key] ? <RemoveIcon /> : <AddIcon />}
                  </FAQItem>
                  <FAQDescription
                    $isExpanded={expandedItems[key]}
                    height={contentRefs.current[key]?.scrollHeight}
                    ref={el => (contentRefs.current[key] = el)}
                  >
                    {faq.description}
                  </FAQDescription>
                  {key !== faqs.length - 1 && <DividerNoMargin />}
                </Fragment>
              )
            })}
          </FAQBox>
        </SpacingBox>
      </CentralBox>
      <RightBox>
        <ChallengeCard />
        <ExamStatusCard examReports={uniqueReports} />
      </RightBox>

      {isOpen && <ExpireSurveyDialog open={true} setOpen={setIsOpen} />}
    </DashboardBox>
  )
}

export default View
